import type {Lang, Widget} from '@yandex-int/messenger.widget'
import React, {useEffect, useState} from 'react'
import {useTheme} from 'src/shared/hooks/useTheme'

import {loadMessengerWidgetBundle} from './loader'

export const RouteSupportWidgetContext = React.createContext<Widget | null>(null)

type Props = {
  serviceID: number
  lang?: string
  country?: string
  debug?: string
  simple?: boolean
}

export const RouteSupportWidgetProvider: React.FC<React.PropsWithChildren<Props>> = ({
  simple,
  country,
  debug,
  serviceID,
  lang,
  children,
}) => {
  const theme = useTheme()
  const [widget, setWidget] = useState<Widget | null>(null)

  useEffect(() => {
    let supportWidget: Widget | null = null

    window.requestIdleCallback(
      () =>
        loadMessengerWidgetBundle().then(({Widget, YandexSingleChatConfig, popupUIFactory, supportPopupUIFactory}) => {
          /**
           * Без autocloseable попап на desktop невозможно закрыть:
           * собственный крестик виджета скрывается после загрузки чата,
           * а хедер мессенджера с крестиком вырезан флагом disableNavigation.
           * @see CARGODEV-85955
           */
          const uiProps = {autocloseable: true}
          const ui = simple ? popupUIFactory(uiProps) : supportPopupUIFactory(uiProps)

          /**
           * Собеседник (guid бота или chatId) и context намеренно не задаются
           * в конфиге: они зависят от support-action конкретного заказа
           * и передаются при открытии через widget.show(params).
           * @see SupportButton
           */
          supportWidget = new Widget(
            new YandexSingleChatConfig({
              serviceId: serviceID,
              lang: lang as Lang,
              debug,
              flags: {
                disableSettingsButton: '1',
                disableOpenInNewTabButton: '1',
                disableNavigation: '1',
                theme: theme === 'dark' ? 'dark' : 'light',
              },
            }),
          ).setUI(ui)

          supportWidget!.init()

          supportWidget.handlers.getEnv.addListener(request => {
            request.response({country})
          })

          ui.mount()

          setWidget(supportWidget)
        }),
      {timeout: 1000},
    )

    return () => {
      supportWidget?.destroy()
    }
  }, [simple, debug, lang, country, serviceID, theme])

  // Escape закрывает попап: встроенный хоткей живёт в хедере iframe мессенджера,
  // который скрыт флагами конфига, поэтому обрабатываем на стороне страницы
  useEffect(() => {
    if (!widget) return

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        widget.hide()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [widget])

  return <RouteSupportWidgetContext.Provider value={widget}>{children}</RouteSupportWidgetContext.Provider>
}
